import { componentText } from '@schmancy/mixins/index'
import { mutationObserver } from '@schmancy/utils/rxjs-utils'
import type { Observable } from 'rxjs'

/**
 * The remove control's accessible name on a chip given no `removeLabel`.
 *
 * The ladder is the one `nameFor(row)` resolves drag announcements through in
 * the reorder directive: the text the chip renders first, because that is the
 * word the reader sees on it, and `value` only behind that, since a chip's
 * value is as often an opaque id as it is the text on the chip. Only the
 * default slot's nodes count — a `leading` icon or a `trailing` mark would
 * otherwise run into the name as one word.
 */
export function removeName(chip: HTMLElement & { value: string }): string {
	const text = Array.from(chip.childNodes)
		// Text and elements only. A comment's own textContent is its data, and a
		// chip whose label arrives from a Lit binding has Lit's marker comments
		// sitting right beside that text — so reading childNodes wholesale names
		// the chip `Remove ?lit$1234$ Anna Weber`.
		.filter(node => node.nodeType === Node.TEXT_NODE || (node instanceof Element && !node.hasAttribute('slot')))
		.map(node => node.textContent ?? '')
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim()
	const name = text || chip.value
	return name ? componentText('chip.remove', { name }) : componentText('chip.removeUnnamed')
}

/**
 * Emits when the text a chip names itself by changes in place.
 *
 * The half `slotchange` structurally cannot see: a slot fires when its ASSIGNED
 * NODES change, and Lit updates a text binding by writing `data` on the Text
 * node already sitting there — so a chip whose label arrives as `${label}` keeps
 * whatever name it was first given while the visible text moves on, which is
 * every chip in a `repeat()` and every chip whose label is translated. Node-set
 * changes stay `slotchange`'s, so the two never answer the same question.
 */
export function chipTextChanges(chip: HTMLElement): Observable<MutationRecord[]> {
	return mutationObserver(chip, { characterData: true, subtree: true })
}
